/**
 * Hook para contador regresivo hasta la próxima ejecución
 * Optimizado para actualizar solo cuando es necesario
 */
import { useState, useEffect, useRef, useCallback } from 'react'

interface UseCronTaskCountdownOptions {
  nextExecutionISO: string | null
  enabled: boolean
}

interface CountdownState {
  hours: number
  minutes: number
  seconds: number
  humanReadable: string
  isExpired: boolean
}

/**
 * Hook que calcula y actualiza el contador regresivo
 * Solo actualiza cuando la pestaña está visible
 */
export function useCronTaskCountdown({
  nextExecutionISO,
  enabled,
}: UseCronTaskCountdownOptions): CountdownState {
  const [countdown, setCountdown] = useState<CountdownState>({
    hours: 0,
    minutes: 0,
    seconds: 0,
    humanReadable: '--',
    isExpired: false,
  })

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const isVisibleRef = useRef(true)

  const calculateCountdown = useCallback(() => {
    if (!nextExecutionISO) {
      return {
        hours: 0,
        minutes: 0,
        seconds: 0,
        humanReadable: '--',
        isExpired: false,
      }
    }

    const now = new Date().getTime()
    const next = new Date(nextExecutionISO).getTime()
    const diff = next - now

    if (diff <= 0) {
      return {
        hours: 0,
        minutes: 0,
        seconds: 0,
        humanReadable: 'Ejecutando...',
        isExpired: true,
      }
    }

    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    const parts: string[] = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`)

    return {
      hours,
      minutes,
      seconds,
      humanReadable: parts.join(' '),
      isExpired: false,
    }
  }, [nextExecutionISO])

  useEffect(() => {
    if (!enabled || !nextExecutionISO) {
      setCountdown({
        hours: 0,
        minutes: 0,
        seconds: 0,
        humanReadable: '--',
        isExpired: false,
      })
      return
    }

    // Calcular inicial
    setCountdown(calculateCountdown())

    // Manejar visibilidad
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden
      if (!document.hidden) {
        setCountdown(calculateCountdown())
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Actualizar cada segundo solo si está visible
    intervalRef.current = setInterval(() => {
      if (isVisibleRef.current) {
        setCountdown(calculateCountdown())
      }
    }, 1000)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [enabled, nextExecutionISO, calculateCountdown])

  return countdown
}

